import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";

// Boost external image URLs to higher resolution & quality
function enhanceUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("unsplash.com")) {
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      u.searchParams.set("w", "2400");
      u.searchParams.set("q", "88");
      return u.toString();
    }
    if (u.hostname.includes("pexels.com")) {
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", "2400");
      u.searchParams.set("dpr", "2");
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

export default function Hero({ images, settings }) {
  const [active, setActive] = useState(0);
  const slides =
    images && images.length > 0
      ? images
      : [
          {
            id: "fallback-1",
            data_url:
              "https://images.pexels.com/photos/29609013/pexels-photo-29609013.jpeg",
            caption: "",
          },
        ];

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, 5500);
    return () => clearInterval(id);
  }, [slides.length]);

  const orderUrl = settings?.order_url || "#";
  const phone = settings?.phone || "(732) 928-9115";
  const telHref = `tel:${phone.replace(/[^0-9+]/g, "")}`;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[96vh] w-full overflow-hidden flex items-center justify-center"
    >
      {/* Slides */}
      <div className="absolute inset-0">
        {slides.map((s, i) => (
          <div
            key={s.id || i}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ${
              i === active ? "opacity-100 kenburns-active" : "opacity-0"
            }`}
          >
            <img
              src={enhanceUrl(s.data_url)}
              alt={s.caption || "Emilio's Pizza"}
              className="w-full h-full object-cover"
              style={{
                filter:
                  "saturate(1.18) contrast(1.06) brightness(1.06)",
              }}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
            />
          </div>
        ))}
        {/* Softer vignette to keep images bright while ensuring text contrast */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 60%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.80) 100%)",
          }}
        />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-16 md:pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center justify-center gap-3 text-white/90 text-xs tracking-[0.3em] uppercase mb-8"
        >
          <span className="h-px w-10 bg-[#F5D7A6]" />
          Jackson Township · Since 2001
          <span className="h-px w-10 bg-[#F5D7A6]" />
        </motion.div>

        {/* LOGO — the star of the show */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative inline-block"
        >
          {/* Warm glow behind the logo */}
          <div
            aria-hidden
            className="absolute inset-0 -m-10 rounded-full blur-3xl opacity-70"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(245,215,166,0.55) 0%, rgba(168,60,39,0.20) 45%, transparent 70%)",
            }}
          />
          <motion.img
            src="/logo-emilios.jpg"
            alt="Emilio's Restaurant & Pizzeria"
            data-testid="hero-logo"
            className="relative mx-auto w-[300px] sm:w-[440px] md:w-[560px] lg:w-[640px] h-auto rounded-2xl shadow-2xl shadow-black/60"
            animate={{ y: [0, -6, 0] }}
            transition={{
              y: {
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-8 text-white text-lg md:text-2xl font-serif italic max-w-2xl mx-auto drop-shadow-lg"
        >
          {settings?.tagline ||
            "Handmade dough. Wood-fired flavor. A slice of Italy in Jackson Township."}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-btn"
            className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-white font-semibold text-base hover:bg-[var(--terracotta-hover)] transition-all hover:-translate-y-0.5 shadow-lg shadow-black/40"
          >
            Order Online
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
          <a
            href={telHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/50 text-white font-medium hover:bg-white/10 backdrop-blur-sm transition-colors"
          >
            <Phone size={16} /> {phone}
          </a>
        </motion.div>

        {/* Slide indicators */}
        {slides.length > 1 && (
          <div className="mt-16 flex items-center justify-center gap-2" data-testid="hero-indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`hero-indicator-${i}`}
                aria-label={`Show slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
